import { useTranslation } from 'react-i18next'
import { Alert, AlertTitle, AlertDescription } from '@agion/ui'
import { Clock } from 'lucide-react'
import { formatDuration } from '@/core/infra_utils'
import type { InfraOperation, OperationStatus } from '@/core/infra_types'

interface OperationOutcomeProps {
  status: OperationStatus | null
  operation: InfraOperation | null
}

const TERMINAL_STATUSES = new Set<string>(['succeeded', 'completed', 'failed', 'cancelled'])

const variantMap: Record<string, 'success' | 'error' | 'warning'>  = {
  succeeded: 'success',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
}

export function OperationOutcome({ status, operation }: OperationOutcomeProps) {
  const { t } = useTranslation(['pages'])

  if (!status || !TERMINAL_STATUSES.has(status)) return null

  const variant = variantMap[status] ?? 'info'
  const duration = formatDuration(operation?.startedAt, operation?.completedAt)
  const titleKey = status === 'failed' ? 'failed' : status === 'cancelled' ? 'cancelled' : 'succeeded'

  return (
    <Alert variant={variant} className="mt-4" role="alert">
      <AlertTitle>
        {t(`pages:operations.outcome.${titleKey}`, `Operation ${titleKey}`)}
      </AlertTitle>
      <AlertDescription>
        <span className="flex flex-col gap-1">
          {status === 'failed' && operation?.errorMessage && (
            <span>{operation.errorMessage}</span>
          )}
          {duration && (
            <span className="flex items-center gap-1 text-xs opacity-75">
              <Clock size={12} />
              {t('pages:operations.outcome.duration', 'Duration')}: {duration}
            </span>
          )}
        </span>
      </AlertDescription>
    </Alert>
  )
}
